import { useCallback, useEffect, useRef, useState } from "react";
import { timelineCandidates } from "../lib/active";
import { GROUPS, groupOfKind } from "../lib/colors";
import { CT_UMBRELLA, type Conflicts, type Stats } from "../lib/data";
import { fmtDeaths, fmtInt } from "../lib/format";
import { togglePlay } from "../lib/replayEngine";
import { MAX_SPAN, MIN_SPAN, useApp, visibleRange, type ReplayRun } from "../lib/store";
import { chapterAt } from "../lib/replay";
import { formatInstant, ticks, yearLabel, yearsLabel } from "../lib/time";
import { packLanes, type LaneBar } from "./lanes";

export type Metric = "deaths" | "active" | "events";
export const METRIC_LABEL: Record<Metric, string> = {
  deaths: "Battle deaths per year",
  active: "Conflicts going on",
  events: "Recorded battles & events",
};

const STRIP_H = 46, LANE_H = 13, LANE_GAP = 3, AXIS_H = 22, TOP_PAD = 6;

interface Geometry { w: number; h: number; lanes: number }

function geometry(w: number, h: number): Geometry {
  const lanes = Math.max(2, Math.floor((h - STRIP_H - AXIS_H - TOP_PAD - 6) / (LANE_H + LANE_GAP)));
  return { w, h, lanes };
}

/** Which metric suits a range by default: deaths where most of the range has estimates (1816→), else battles. */
export function defaultMetric(a: number, b = a + 1): Metric {
  return (b - Math.max(a, 1816)) / (b - a) >= 0.6 ? "deaths" : "events";
}

export function Timeline() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 800, h: 150 });
  const [metric, setMetric] = useState<Metric | "auto">("auto");
  const [tip, setTip] = useState<{ x: number; y: number; text: string[] } | null>(null);
  const bars = useRef<LaneBar[]>([]);
  const anim = useRef<number | null>(null);
  const drag = useRef<{ x: number; t: number; moved: boolean; lastX: number; lastTime: number; v: number; pointers: Map<number, number>; pinch?: { d: number; span: number } } | null>(null);

  const conflicts = useApp((s) => s.conflicts);
  const stats = useApp((s) => s.stats);

  // size
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ w: Math.round(e.contentRect.width), h: Math.round(e.contentRect.height) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // draw on every relevant change
  const draw = useCallback(() => {
    const cv = canvasRef.current;
    const st = useApp.getState();
    if (!cv || !st.conflicts || !st.stats) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const { w, h } = size;
    if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) {
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
    }
    const ctx = cv.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const g = geometry(w, h);
    const [a, b] = visibleRange(st.t, st.span);
    const m: Metric = metric === "auto" ? defaultMetric(a, b) : metric;
    bars.current = render(ctx, g, a, b, st.t, st.now, st.conflicts, st.stats, m, st.selected, st.hoverConflict, st.kinds,
      st.replay, st.playing);
  }, [size, metric]);

  useEffect(() => {
    draw();
    return useApp.subscribe((s, p) => {
      if (s.t !== p.t || s.span !== p.span || s.selected !== p.selected || s.hoverConflict !== p.hoverConflict ||
          s.kinds !== p.kinds || s.conflicts !== p.conflicts || s.replay !== p.replay || s.playing !== p.playing) requestAnimationFrame(draw);
    });
  }, [draw, conflicts, stats]);

  // --- interaction --------------------------------------------------------------------------
  const timeAt = (x: number) => {
    const { t, span } = useApp.getState();
    const [a0] = visibleRange(t, span);
    return a0 + (x / size.w) * span;
  };
  const stopAnim = () => { if (anim.current != null) cancelAnimationFrame(anim.current); anim.current = null; };
  const animateTo = (target: number, ms = 450) => {
    stopAnim();
    const start = useApp.getState().t, t0 = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / ms), e = 1 - Math.pow(1 - k, 3);
      useApp.getState().setT(start + (target - start) * e);
      anim.current = k < 1 ? requestAnimationFrame(step) : null;
    };
    anim.current = requestAnimationFrame(step);
  };
  const fling = (v: number) => {
    stopAnim();
    let vel = v, last = performance.now();
    const step = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      const st = useApp.getState();
      st.setT(st.t + vel * dt);
      vel *= Math.pow(0.04, dt);
      anim.current = Math.abs(vel) > st.span * 0.01 ? requestAnimationFrame(step) : null;
    };
    anim.current = requestAnimationFrame(step);
  };

  const onWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    stopAnim();
    const st = useApp.getState();
    if (e.ctrlKey || e.metaKey) {
      st.setSpan(st.span * Math.exp(e.deltaY * 0.0045));
    } else {
      const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      const px = e.deltaMode === 1 ? d * 16 : d;
      st.setT(st.t + (px / size.w) * st.span * 0.9);
    }
    st.setPlaying(false);
  }, [size.w]);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    cv.addEventListener("wheel", onWheel, { passive: false });
    return () => cv.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  const barAt = (x: number, y: number) =>
    bars.current.find((bb) => x >= bb.x0 - 2 && x <= bb.x1 + 2 && y >= bb.y - 2 && y <= bb.y + LANE_H + 2);

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    stopAnim();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const st = useApp.getState();
    if (!drag.current) drag.current = { x, t: st.t, moved: false, lastX: x, lastTime: performance.now(), v: 0, pointers: new Map() };
    drag.current.pointers.set(e.pointerId, x);
    if (drag.current.pointers.size === 2) {
      const [p1, p2] = [...drag.current.pointers.values()];
      drag.current.pinch = { d: Math.abs(p1 - p2) || 1, span: st.span };
    }
    st.setPlaying(false);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const d = drag.current;
    const st = useApp.getState();
    if (d && d.pointers.has(e.pointerId)) {
      d.pointers.set(e.pointerId, x);
      if (d.pinch && d.pointers.size === 2) {
        const [p1, p2] = [...d.pointers.values()];
        st.setSpan(d.pinch.span * (d.pinch.d / Math.max(10, Math.abs(p1 - p2))));
        d.moved = true;
        return;
      }
      const dx = x - d.x;
      if (Math.abs(dx) > 3) d.moved = true;
      if (d.moved) {
        st.setT(d.t - (dx / size.w) * st.span);
        const now = performance.now(), dt = (now - d.lastTime) / 1000;
        if (dt > 0) d.v = 0.7 * d.v + 0.3 * (-(x - d.lastX) / size.w * st.span / dt);
        d.lastX = x;
        d.lastTime = now;
      }
      setTip(null);
      return;
    }
    // hover
    const bar = barAt(x, y);
    st.setHoverConflict(bar ? bar.i : -1);
    if (bar && st.conflicts) {
      const c = st.conflicts;
      setTip({ x, y, text: [c.name[bar.i], yearsLabel(c.t0[bar.i], c.t1[bar.i], !!c.og[bar.i]) + (c.d[bar.i] > 0 ? ` · ${fmtDeaths(c.d[bar.i])} deaths` : "")] });
    } else if (y < STRIP_H + TOP_PAD && st.stats) {
      const yr = Math.floor(timeAt(x));
      const m: Metric = metric === "auto" ? defaultMetric(timeAt(0), timeAt(size.w)) : metric;
      setTip({ x, y, text: stripTooltip(st.stats, yr, m) });
    } else setTip(null);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    if (!d) return;
    d.pointers.delete(e.pointerId);
    if (d.pointers.size > 0) return;
    drag.current = null;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const st = useApp.getState();
    if (d.moved) {
      if (Math.abs(d.v) > st.span * 0.15) fling(d.v);
      return;
    }
    const bar = barAt(x, y);
    if (bar) st.select(bar.i, { fly: true });
    animateTo(timeAt(x));
  };
  const onDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const bar = barAt(e.clientX - rect.left, e.clientY - rect.top);
    const st = useApp.getState();
    if (bar && st.conflicts) {
      const c = st.conflicts, t0 = c.t0[bar.i], t1 = c.t1[bar.i], pad = Math.max((t1 - t0) * 0.15, 0.08);
      stopAnim();
      st.setRange(t0 - pad, t1 + pad);
      st.setT(t0 + (t1 - t0) * 0.02);
    } else {
      st.setSpan(st.span / 2.5);
    }
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    const st = useApp.getState();
    const step = st.span / (e.shiftKey ? 5 : 50);
    let handled = true;
    if (e.key === "ArrowRight") st.setT(st.t + step);
    else if (e.key === "ArrowLeft") st.setT(st.t - step);
    else if (e.key === "+" || e.key === "=" || e.key === "ArrowUp") st.setSpan(st.span / 1.5);
    else if (e.key === "-" || e.key === "_" || e.key === "ArrowDown") st.setSpan(st.span * 1.5);
    else if (e.key === "End") animateTo(st.now);
    else if (e.key === "Home") animateTo(-2999);
    else if (e.key === " ") togglePlay();
    else handled = false;
    if (handled) e.preventDefault();
  };

  const t = useApp((s) => s.t), span = useApp((s) => s.span);
  const [a, b] = visibleRange(t, span);
  const m: Metric = metric === "auto" ? defaultMetric(a, b) : metric;

  return (
    <div className="timeline" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        className="timeline-canvas"
        style={{ width: size.w, height: size.h }}
        tabIndex={0}
        role="slider"
        aria-label="Timeline. Arrow keys move through time, plus and minus zoom, space plays."
        aria-valuemin={-2999}
        aria-valuemax={Math.floor(useApp.getState().now)}
        aria-valuenow={Math.floor(t)}
        aria-valuetext={formatInstant(t, span)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { drag.current = null; }}
        onPointerLeave={() => { setTip(null); useApp.getState().setHoverConflict(-1); }}
        onDoubleClick={onDoubleClick}
        onKeyDown={onKeyDown}
      />
      <button className="strip-metric" onClick={() => setMetric(nextMetric(m))} title="Change what the strip shows">
        {METRIC_LABEL[m]} <span aria-hidden>▾</span>
      </button>
      {tip && (
        <div className="timeline-tip" style={{ left: Math.min(Math.max(tip.x, 90), size.w - 90), top: Math.max(tip.y - 8, 4) }}>
          {tip.text.map((x, k) => <div key={k} className={k === 0 ? "tip-title" : "tip-line"}>{x}</div>)}
        </div>
      )}
    </div>
  );
}

const nextMetric = (m: Metric): Metric => (m === "deaths" ? "active" : m === "active" ? "events" : "deaths");

function stripTooltip(stats: Stats, yr: number, m: Metric): string[] {
  const k = yr - stats.y0;
  if (k < 0 || k >= stats.active.length) return [];
  const label = yearLabel(yr);
  if (m === "deaths") {
    const d = stats.deaths[k];
    const src = stats.deaths_src.find((s) => yr >= s.from && yr <= s.to)?.src;
    return d == null ? [label, "No battle-death estimates before 1816"] :
      [label, `${fmtDeaths(d)} battle deaths`, src === "cow" ? "Correlates of War (wars ≥1,000 battle deaths)" : "UCDP (all organised violence)" + (yr >= 2026 ? " · provisional, Jan–Sep" : "")];
  }
  if (m === "active") return [label, `${fmtInt(stats.active[k])} conflicts going on`, `${fmtInt(stats.wars[k])} of them wars with ≥1,000 deaths a year`];
  return [label, `${fmtInt(stats.events[k])} recorded battles & events`];
}

// --- drawing --------------------------------------------------------------------------------------

function render(ctx: CanvasRenderingContext2D, g: Geometry, a: number, b: number, t: number, now: number,
  c: Conflicts, stats: Stats, metric: Metric, selected: number, hover: number, kinds: boolean[],
  replay: ReplayRun | null, playing = false): LaneBar[] {
  const { w, h } = g;
  const css = getComputedStyle(document.documentElement);
  const col = (v: string, f: string) => css.getPropertyValue(v).trim() || f;
  const bg = col("--tl-bg", "#0d131a"), ink = col("--text-2", "#a7b0bc"), muted = col("--text-3", "#6b7684"),
    grid = col("--tl-grid", "#1c2530"), strip = col("--tl-strip", "#7f8fa3");
  const px = (x: number) => ((x - a) / (b - a)) * w;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  // future (after today) hatched
  const xn = px(now);
  if (xn < w) {
    ctx.save();
    ctx.fillStyle = "rgba(255,255,255,0.025)";
    ctx.fillRect(Math.max(0, xn), 0, w - Math.max(0, xn), h);
    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 1;
    for (let x = Math.max(0, xn) - h; x < w; x += 10) {
      ctx.beginPath();
      ctx.moveTo(x, h);
      ctx.lineTo(x + h, 0);
      ctx.stroke();
    }
    ctx.restore();
  }

  // axis ticks & grid
  const tk = ticks(a, b, w);
  ctx.font = "11px 'Inter Variable', system-ui, sans-serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  for (const x of tk) {
    const X = Math.round(px(x.t)) + 0.5;
    ctx.strokeStyle = x.major ? "#243040" : grid;
    ctx.beginPath();
    ctx.moveTo(X, TOP_PAD);
    ctx.lineTo(X, h - AXIS_H + (x.major ? 6 : 3));
    ctx.stroke();
    ctx.fillStyle = x.major ? ink : muted;
    ctx.font = `${x.major ? 600 : 400} 11px 'Inter Variable', system-ui, sans-serif`;
    ctx.fillText(x.label, X, h - AXIS_H / 2 + 1);
  }

  // density strip
  drawStrip(ctx, w, a, b, stats, metric, strip, px);

  // lanes
  const top = STRIP_H + TOP_PAD + 4;
  const minW = (b - a) / w; // years per pixel
  const candidates = timelineCandidates(c, a, b, 400)
    .filter((i) => kinds[c.kind[i]] && ((c.t1[i] - c.t0[i]) / minW >= 5 || c.sig[i] >= 0.55));
  const placed = packLanes(c, candidates, a, b, w, g.lanes, top, LANE_H, LANE_GAP, ctx);
  for (const bar of placed) {
    const i = bar.i, grp = groupOfKind(c.kind[i]);
    const isSel = i === selected, isHov = i === hover;
    const umbrella = c.ct[i] === CT_UMBRELLA;
    ctx.globalAlpha = umbrella ? 0.45 : isSel || isHov ? 1 : 0.82;
    ctx.fillStyle = GROUPS[grp].hex;
    for (const [x0, x1] of bar.segs) roundRect(ctx, x0, bar.y + (umbrella ? 4 : 0), Math.max(2, x1 - x0), umbrella ? 5 : LANE_H, 3);
    ctx.globalAlpha = 1;
    if (isSel || isHov) {
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = isSel ? 1.5 : 1;
      for (const [x0, x1] of bar.segs) { roundRectPath(ctx, x0 - 1, bar.y - 1, Math.max(2, x1 - x0) + 2, LANE_H + 2, 4); ctx.stroke(); }
    }
    if (bar.label) {
      ctx.fillStyle = "#0b1016";
      ctx.font = "600 10.5px 'Inter Variable', system-ui, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(bar.label, bar.lx, bar.y + LANE_H / 2 + 0.5);
    }
  }

  // a replay: the span it plays and its chapters, just above the axis
  if (replay) {
    const y = h - AXIS_H - 4, accent = col("--accent", "#e9c46a");
    const x0 = Math.max(0, px(replay.t0)), x1 = Math.min(w, px(replay.t1));
    ctx.fillStyle = accent;
    ctx.globalAlpha = 0.35;
    if (x1 > x0) ctx.fillRect(x0, y - 1, x1 - x0, 3);
    ctx.globalAlpha = 1;
    const cur = chapterAt(replay.chapters, t);
    replay.chapters.forEach((ch, k) => {
      const x = px(ch.t);
      if (x < -6 || x > w + 6) return;
      const r = k === cur ? 5 : 3.5;
      ctx.fillStyle = k === cur ? "#ffffff" : accent;
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - r, y);
      ctx.closePath();
      ctx.fill();
    });
  }

  // today marker
  if (xn >= 0 && xn <= w) {
    ctx.strokeStyle = col("--accent", "#e9c46a");
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(Math.round(xn) + 0.5, 0);
    ctx.lineTo(Math.round(xn) + 0.5, h - AXIS_H);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = col("--accent", "#e9c46a");
    ctx.font = "600 10px 'Inter Variable', system-ui, sans-serif";
    ctx.textAlign = xn > w - 40 ? "right" : "left";
    ctx.fillText("today", xn + (xn > w - 40 ? -4 : 4), 9);
  }
  // playhead needle (glowing while history plays)
  const xt = Math.round(px(t)) + 0.5;
  if (playing) {
    const glow = ctx.createLinearGradient(xt - 26, 0, xt + 26, 0);
    glow.addColorStop(0, "rgba(233,196,106,0)");
    glow.addColorStop(0.5, "rgba(233,196,106,0.16)");
    glow.addColorStop(1, "rgba(233,196,106,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(xt - 26, 0, 52, h);
    ctx.shadowColor = "rgba(233,196,106,0.9)";
    ctx.shadowBlur = 8;
  }
  ctx.strokeStyle = "#e6e9ee";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(xt, 0);
  ctx.lineTo(xt, h);
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.fillStyle = "#e6e9ee";
  ctx.beginPath();
  ctx.moveTo(xt - 6, 0);
  ctx.lineTo(xt + 6, 0);
  ctx.lineTo(xt, 7);
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
  return placed;
}

function drawStrip(ctx: CanvasRenderingContext2D, w: number, a: number, b: number, stats: Stats, metric: Metric,
  color: string, px: (x: number) => number) {
  const arr: (number | null)[] = metric === "deaths" ? stats.deaths : metric === "active" ? stats.active : stats.events;
  const y0 = stats.y0;
  const bins = Math.max(20, Math.floor(w / 3));
  const binYears = (b - a) / bins;
  const vals: (number | null)[] = [];
  for (let k = 0; k < bins; k++) {
    const t0 = a + k * binYears, t1 = t0 + binYears;
    let sum = 0, n = 0, any = false;
    for (let y = Math.floor(t0); y <= Math.floor(t1 - 1e-9); y++) {
      const v = arr[y - y0];
      if (v == null || y < y0 || y - y0 >= arr.length) continue;
      const ov = Math.min(t1, y + 1) - Math.max(t0, y);
      sum += v * ov;
      n += ov;
      any = true;
    }
    vals.push(any && n > 0 ? sum / n : null);
  }
  const H = STRIP_H - 6, base = TOP_PAD + STRIP_H - 2;
  // Deaths on a fixed square-root scale (World War II's peak ≈ full height), so magnitudes compare across eras;
  // counts on a linear scale of what is visible.
  const DEATHS_MAX = Math.sqrt(3.3e6);
  const max = metric === "deaths" ? DEATHS_MAX : Math.max(1, ...vals.map((v) => v ?? 0));
  const yOf = (v: number) => base - (metric === "deaths" ? Math.sqrt(Math.max(0, v)) / max : v / max) * H;
  if (metric === "deaths") {
    ctx.font = "9.5px 'Inter Variable', system-ui, sans-serif";
    ctx.textAlign = "right";
    for (const [v, l] of [[100000, "100k"], [1000000, "1M"], [3000000, "3M"]] as [number, string][]) {
      const Y = Math.round(yOf(v)) + 0.5;
      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.beginPath();
      ctx.moveTo(0, Y);
      ctx.lineTo(w, Y);
      ctx.stroke();
      ctx.fillStyle = "rgba(167,176,188,0.6)";
      ctx.fillText(l, w - 4, Y - 5);
    }
  }
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.55;
  const bw = w / bins;
  vals.forEach((v, k) => {
    if (v == null || v <= 0) return;
    const Y = yOf(v);
    ctx.fillRect(k * bw, Y, Math.max(1, bw - 0.6), base - Y);
  });
  ctx.globalAlpha = 1;
  if (metric === "deaths" && a < 1816) {
    const x = px(1816);
    ctx.fillStyle = "rgba(167,176,188,0.55)";
    ctx.font = "italic 10px 'Inter Variable', system-ui, sans-serif";
    ctx.textAlign = "right";
    if (x > 120) ctx.fillText("no death estimates before 1816", Math.min(x - 6, w - 6), TOP_PAD + STRIP_H / 2);
  }
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  roundRectPath(ctx, x, y, w, h, r);
  ctx.fill();
}

export { MAX_SPAN, MIN_SPAN };
